import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
// eslint-disable-next-line import/no-unresolved
import { parseGooglePlaceLocation } from "common/helpers/googlePlaceAutoComplete";

/**
 *  DEPRECATED -- use GooglePlacesAutocomplete instead
 */

const MAPS_POLL_INTERVAL_MS = 100;

let autoComplete;

const handlePlaceSelect = async (updateQuery, withOverride, stateShort) => {
  const {
    address_components: addressComponents,
    formatted_address: formattedAddress,
  } = autoComplete.getPlace();
  const parsedLocation = withOverride
    ? parseGooglePlaceLocation(addressComponents, stateShort)
    : formattedAddress;

  await updateQuery(parsedLocation);
};

const handleScriptLoad = (
  updateQuery,
  autoCompleteRef,
  googlePlaceTypeCollection,
  withOverride,
  stateShort
) => {
  autoComplete = new window.google.maps.places.Autocomplete(
    autoCompleteRef.current,
    { types: [googlePlaceTypeCollection] }
  );
  autoComplete.setFields(["address_components", "formatted_address"]);
  autoComplete.addListener("place_changed", () =>
    handlePlaceSelect(updateQuery, withOverride, stateShort)
  );
};

const LocationInput = ({
  name,
  className = "",
  placeholder = "Enter/Select full address",
  onChange,
  value = undefined,
  withOverride = false,
  stateShort = false,
  googlePlaceTypeCollection = "address",
  isDisabled = false,
}) => {
  const autoCompleteRef = useRef(null);

  useEffect(() => {
    // Maps loads async/defer in the layout, so wait until it's ready to avoid crashing on undefined window.google.
    const isMapsReady = () => Boolean(window.google?.maps?.places);

    if (isMapsReady()) {
      return handleScriptLoad(
        onChange,
        autoCompleteRef,
        googlePlaceTypeCollection,
        withOverride,
        stateShort
      );
    }

    const intervalId = setInterval(() => {
      if (!isMapsReady()) return;

      clearInterval(intervalId);
      handleScriptLoad(
        onChange,
        autoCompleteRef,
        googlePlaceTypeCollection,
        withOverride,
        stateShort
      );
    }, MAPS_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, []);

  if (value !== undefined) {
    // Controlled input
    return (
      <input
        className={className}
        ref={autoCompleteRef}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        name={name}
        value={value}
        disabled={isDisabled}
      />
    );
  }
  // Plain (non-controlled) input for backwards-compatibility
  return (
    <input
      className={className}
      ref={autoCompleteRef}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      name={name}
      disabled={isDisabled}
    />
  );
};

LocationInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  className: PropTypes.string,
  withOverride: PropTypes.bool,
  stateShort: PropTypes.bool,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  googlePlaceTypeCollection: PropTypes.string,
  isDisabled: PropTypes.bool,
};

export default LocationInput;
